import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { TreeSelect } from "antd";
import "antd/dist/reset.css";
import api from "../../../core/api/config";
import {
  setAcessos,
  setAcessoAtivo,
  setPerfilAtivo,
} from "../../../core/redux/access/accessActions";

const SelecaoEstabelecimentoModal = ({ show, onClose, onConfirm }) => {
  const dispatch = useDispatch();
  const { acessos, acessoAtivo, perfilAtivo } = useSelector(
    (state) => state.access,
  );

  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);

  const getNodeId = (node) => node?.idOrganograma ?? node?.id;
  const getNodeChildren = (node) => node?.filhos ?? node?.children ?? [];

  /* ===============================
     CARREGA ESTABELECIMENTOS
  =============================== */
  useEffect(() => {
    const carregar = async () => {
      try {
        setLoading(true);
        setErro("");

        const { data } = await api.get("/api/user/show");
        const user = data?.user;

        if (!user?.id) throw new Error("Usuário não identificado");

        if (!user?.ativo) {
          setErro("Usuário inativo. Entre em contato com o suporte.");
          return;
        }

        const response = await api.get(
          `/api/tracking/usuario/acesso?userId=${user.id}`,
        );

        dispatch(setAcessos(response.data || []));
        dispatch(setPerfilAtivo(user));
      } catch (e) {
        console.error(e);
        setErro("Erro ao carregar estabelecimentos.");
      } finally {
        setLoading(false);
      }
    };

    if (show && (!acessos || acessos.length === 0)) carregar();
  }, [show, dispatch, acessos]);

  /* ===============================
     MAPA DE NÓS
  =============================== */
  const nodeMap = useMemo(() => {
    const map = {};
    const walk = (nodes) => {
      nodes.forEach((n) => {
        const nodeId = getNodeId(n);
        if (nodeId != null) {
          map[nodeId] = {
            ...n,
            idOrganograma: nodeId,
          };
        }

        const children = getNodeChildren(n);
        if (children.length) walk(children);
      });
    };
    walk(acessos);
    return map;
  }, [acessos]);

  const treeData = useMemo(() => {
    const buildTree = (nodes) =>
      nodes.map((node) => ({
        title: `${node.descricao}${node.cnes ? ` (${node.cnes})` : ""}`,
        value: getNodeId(node),
        key: getNodeId(node),
        children: getNodeChildren(node).length
          ? buildTree(getNodeChildren(node))
          : [],
      }));

    return buildTree(acessos);
  }, [acessos]);

  const isDRS = Number(perfilAtivo?.tipo) === 3;

  const treeDataFinal = useMemo(() => {
    if (isDRS) {
      return [
        {
          title: "TODOS",
          value: "TODOS",
          key: "TODOS",
        },
        ...treeData,
      ];
    }
    return treeData;
  }, [treeData, isDRS]);

  /* ===============================
     CONFIRMAÇÃO
  =============================== */
  const confirmar = (e) => {
    e.preventDefault();

    if (!acessoAtivo) {
      setErro("Selecione um estabelecimento.");
      return;
    }

    try {
      if (acessoAtivo.idOrganograma === "TODOS") {
        dispatch(setAcessoAtivo({ idOrganograma: "TODOS" }));

        localStorage.setItem(
          "acessoAtivo",
          JSON.stringify({ idOrganograma: "TODOS" }),
        );

        onConfirm({ idOrganograma: "TODOS" });
        return;
      }

      dispatch(setAcessoAtivo(acessoAtivo));
      localStorage.setItem("acessoAtivo", JSON.stringify(acessoAtivo));

      // mantém sua regra do cidade_id
      if (acessoAtivo?.cidade) {
        const novoPerfil = {
          ...perfilAtivo,
          cidade_id: acessoAtivo.cidade,
        };

        dispatch(setPerfilAtivo(novoPerfil));
        localStorage.setItem("perfilAtivo", JSON.stringify(novoPerfil));
      }

      onConfirm(acessoAtivo);
    } catch (err) {
      console.error(err);
      setErro("Erro ao confirmar acesso.");
    }
  };

  const handleChange = (value) => {
    if (value === "TODOS") {
      dispatch(
        setAcessoAtivo({
          idOrganograma: "TODOS",
          descricao: "TODOS",
        }),
      );
      return;
    }

    const node = nodeMap[value] || nodeMap[Number(value)];

    if (node) {
      dispatch(setAcessoAtivo(node));
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block">
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Selecione um Estabelecimento</h4>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          {/* BODY */}
          <div
            className="modal-body"
            style={{
              flex: 1,
              overflow: "hidden",
            }}
          >
            {erro && <div className="alert alert-danger">{erro}</div>}
            {loading && <p className="text-center">Carregando...</p>}

            {!loading && (
              <TreeSelect
                showSearch
                style={{ width: "100%" }}
                placeholder="Buscar estabelecimento..."
                allowClear
                value={acessoAtivo?.idOrganograma}
                treeData={treeDataFinal}
                onChange={handleChange}
                popupMatchSelectWidth
                listHeight={450}
                styles={{
                  popup: {
                    root: {
                      maxHeight: 450,
                      overflow: "auto",
                      zIndex: 9999, // resolve sobreposição
                    },
                  },
                }}
                filterTreeNode={(input, node) =>
                  node.title.toLowerCase().includes(input.toLowerCase())
                }
              />
            )}
          </div>

          <div className="modal-footer border-top d-grid gap-2 d-md-flex justify-content-end">
            <button className="btn btn-secondary" onClick={onClose}>
              <i className="feather-x me-2"></i>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={confirmar}>
              <i className="feather-log-in me-2"></i>
              Entrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

SelecaoEstabelecimentoModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default SelecaoEstabelecimentoModal;
